//src/components/event/EventManagerReportViewer.jsx

import React, { useMemo } from "react";
import { useUserPermissions } from "@/hooks/useUserPermissions";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const gbp = (v) => {
  const n = toNum(v);
  return n == null ? "—" : `£${n.toFixed(2)}`;
};

const asDateGB = (v) => {
  try {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return d.toLocaleDateString("en-GB");
  } catch {
    return v ? String(v).slice(0, 10) : "—";
  }
};

const Badge = ({ tone = "neutral", children }) => {
  const cls =
    tone === "good"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
      : tone === "warn"
      ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
      : tone === "bad"
      ? "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
      : "bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/20";

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md border text-xs ${cls}`}>
      {children}
    </span>
  );
};

const Row = ({ label, value, right }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-zinc-200/60 dark:border-zinc-700/60">
    <div className="text-sm text-zinc-600 dark:text-zinc-300">{label}</div>
    <div className="text-sm text-zinc-900 dark:text-white text-right whitespace-pre-wrap">
      {value ?? "—"} {right ? <span className="ml-2">{right}</span> : null}
    </div>
  </div>
);

/**
 * Props:
 * - reportSections: [{ title, body }]
 * - commercial: optional object (recommended)
 *   {
 *     quote_status,        // e.g. "draft" | "sent" | "accepted" | "rejected"
 *     quote_sent_at,
 *     client_total_quote,  // total quoted to client (gross)
 *     deposit_required,
 *     deposit_received,
 *     deposit_received_at,
 *     payment_status,      // e.g. "unpaid" | "part_paid" | "paid"
 *     amount_paid,
 *     balance_due,
 *     payment_reference,
 *     payment_notes
 *   }
 */
const EventManagerReportViewer = ({ reportSections, commercial }) => {
  const { canViewManagerReports } = useUserPermissions();

  const summary = useMemo(() => {
    const c = commercial || {};

    const quoteTotal = toNum(c.client_total_quote);
    const depositReq = toNum(c.deposit_required);
    const depositRec = toNum(c.deposit_received);
    const paid = toNum(c.amount_paid);

    // If backend doesn't provide balance_due, we compute a safe best-effort one:
    const balanceDueComputed =
      quoteTotal == null
        ? null
        : Math.max(0, quoteTotal - (paid ?? 0) - (depositRec ?? 0));

    const balanceDue =
      c.balance_due != null ? toNum(c.balance_due) : balanceDueComputed;

    // Badge logic (purely UI)
    const paymentStatusRaw = String(c.payment_status || "").toLowerCase();
    const quoteStatusRaw = String(c.quote_status || "").toLowerCase();

    const quoteTone =
      quoteStatusRaw.includes("accept") ? "good" :
      quoteStatusRaw.includes("sent") ? "warn" :
      quoteStatusRaw.includes("reject") ? "bad" :
      "neutral";

    const payTone =
      paymentStatusRaw === "paid" ? "good" :
      paymentStatusRaw === "part_paid" ? "warn" :
      paymentStatusRaw === "unpaid" ? "bad" :
      "neutral";

    return {
      quoteTotal,
      depositReq,
      depositRec,
      paid,
      balanceDue,

      quoteStatusRaw,
      paymentStatusRaw,

      quoteTone,
      payTone,
    };
  }, [commercial]);

  if (!canViewManagerReports) {
    return (
      <div className="p-4 text-red-500 border border-red-300 rounded-xl bg-red-50 dark:bg-red-900 dark:text-red-300">
        🚫 You do not have permission to view manager reports for this event.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 dark:bg-zinc-800 border dark:border-zinc-700 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-xl font-semibold text-zinc-800 dark:text-white">
          Event Manager Report
        </h2>

        {/* Quick status badges if data exists */}
        <div className="flex flex-wrap gap-2 justify-end">
          {commercial?.quote_status ? (
            <Badge tone={summary.quoteTone}>Quote: {commercial.quote_status}</Badge>
          ) : null}
          {commercial?.payment_status ? (
            <Badge tone={summary.payTone}>Payment: {commercial.payment_status}</Badge>
          ) : null}
        </div>
      </div>

      {/* Commercial / Payment Status */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50/60 dark:bg-zinc-900/40 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-zinc-800 dark:text-white">
            Commercial / Payment Status
          </div>
          {!commercial ? (
            <Badge tone="warn">Not linked yet</Badge>
          ) : null}
        </div>

        <div className="mt-3">
          <Row
            label="Quote status"
            value={commercial?.quote_status ?? "—"}
            right={commercial?.quote_sent_at ? <Badge>Sent: {asDateGB(commercial.quote_sent_at)}</Badge> : null}
          />
          <Row
            label="Client total (quote)"
            value={gbp(commercial?.client_total_quote)}
          />
          <Row
            label="Deposit required"
            value={gbp(commercial?.deposit_required)}
          />
          <Row
            label="Deposit received"
            value={gbp(commercial?.deposit_received)}
            right={commercial?.deposit_received_at ? <Badge>On: {asDateGB(commercial.deposit_received_at)}</Badge> : null}
          />
          <Row
            label="Amount paid (other payments)"
            value={gbp(commercial?.amount_paid)}
          />
          <Row
            label="Balance due"
            value={summary.balanceDue == null ? "—" : gbp(summary.balanceDue)}
          />
          <Row
            label="Payment reference"
            value={commercial?.payment_reference ?? "—"}
          />
          <div className="pt-2">
            <div className="text-sm text-zinc-600 dark:text-zinc-300">Payment notes</div>
            <div className="mt-1 text-sm text-zinc-900 dark:text-white whitespace-pre-wrap rounded-lg border border-zinc-200/60 dark:border-zinc-700/60 bg-white/70 dark:bg-black/20 p-3">
              {commercial?.payment_notes?.trim?.() ? commercial.payment_notes : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Narrative report sections */}
      <div>
        {reportSections?.length > 0 ? (
          reportSections.map((section, idx) => (
            <div key={idx} className="mb-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                {section.title}
              </h3>
              <p className="text-sm text-gray-700 dark:text-zinc-300 whitespace-pre-wrap">
                {section.body}
              </p>
            </div>
          ))
        ) : (
          <p className="text-gray-500 dark:text-zinc-400">
            No report submitted yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default EventManagerReportViewer;
